/**
 * <LiveLobbyDeviceStrip> — le matériel, en une rangée, sous l'image de l'élève (molecule).
 *
 * Avant une visio, ce qui compte est l'image de soi et le bouton pour entrer ; le matériel se
 * lit d'un coup d'œil dessous, comme les sélecteurs d'appareil d'une salle d'attente de visio.
 * Les quatre lignes du test (`UIDeviceCheckLine`) deviennent quatre chips, chacune au nom de son
 * appareil (« Micro ») et à la tinte de son verdict : le vert réglé, l'ambre ou le rouge en défaut,
 * le gris pas encore mesuré. Ce qui ne va pas se dit en phrase sous la rangée (la page), jamais
 * dans une chip qu'il faudrait couper ; le lecteur d'écran, lui, entend la phrase d'état avec le
 * nom. Une chip en défaut presse quand `onRetry` est là : c'est « Réessayer » sur sa ligne, le
 * geste du bilan d'aujourd'hui.
 *
 * Sans lignes (le matériel déjà vérifié), la rangée dit ce qu'il en est en mots (`note` :
 * « Caméra et micro déjà vérifiés »). Puis, au bout, le seul geste du test qui reste à cette hauteur
 * (`action` : « Vérifier mon matériel », « Revérifier ») ou l'attente dite en mots (`pending` :
 * « On vérifie… »).
 *
 * Elle ne déplie rien : l'étape en cours d'un test, quand il y en a une, se pose dessous, dans
 * la fente de la page.
 *
 * Composes DS pieces only — `<Badge>`, `<Text>`, `<TextButton>`, `<WaitingText>`.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { deviceCheckLineFailing } from "../../../ports/primary/ui/formatters/deviceCheckFormatter"
import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import {
    DEVICE_CHECK_ICONS,
    type UIDeviceCheckLine,
    type UIDeviceCheckStepId,
    type UIDeviceCheckVerdict,
} from "../DeviceCheckRow"

export type LiveLobbyDeviceStripProps = {
    /** Les lignes du test, dans leur ordre : connexion, micro, haut-parleurs, caméra. Défaut : aucune. */
    lines?: UIDeviceCheckLine[]
    /** Ce qui se lit à la place des chips, ou à leur suite — « Caméra et micro déjà vérifiés ». */
    note?: string
    /** Le geste du test à cette hauteur — « Vérifier mon matériel », « Revérifier ». Absent : rien. */
    action?: { label: string; onPress: () => void }
    /** Une vérification tourne sans geste attendu : « On vérifie… », à la place du geste. */
    pending?: boolean
    /** Une chip en défaut presse : « Réessayer » sur son appareil. Absent : aucune ne presse. */
    onRetry?: (id: UIDeviceCheckStepId) => void
    /**
     * L'axe de la rangée : `start` (défaut) la cale à gauche ; `center` la pose sur l'axe de ce qui
     * l'entoure — sous l'image de la salle d'attente, centrée elle aussi.
     */
    align?: "start" | "center"
    testID?: string
}

const TEXTS = {
    pending: "On vérifie…",
    retry: (label: string) => `Réessayer : ${label}`,
}

/** La chip d'un verdict : sa tinte, celle des pastilles du test. */
const VERDICT_BADGE: Record<UIDeviceCheckVerdict, BadgeVariant> = {
    ok: "success",
    warn: "warning",
    bad: "danger",
    pending: "neutral",
}

export function LiveLobbyDeviceStrip(props: LiveLobbyDeviceStripProps) {
    const { lines = [], note, action, pending = false, onRetry, align = "start", testID } = props

    return (
        <View style={styles.row(align)} testID={testID}>
            {lines.map((line) => {
                const chip = (
                    <Badge
                        text={line.label}
                        variant={VERDICT_BADGE[line.skipped === true ? "warn" : line.verdict]}
                        tinted
                        iconName={DEVICE_CHECK_ICONS[line.id]}
                    />
                )
                const said = line.detail !== "" ? `${line.label}, ${line.detail}` : line.label
                return onRetry != null && deviceCheckLineFailing(line) ? (
                    <Pressable
                        key={line.id}
                        onPress={() => onRetry(line.id)}
                        accessibilityRole="button"
                        accessibilityLabel={`${said}. ${TEXTS.retry(line.label)}`}
                    >
                        {chip}
                    </Pressable>
                ) : (
                    <View key={line.id} accessible accessibilityLabel={said}>
                        {chip}
                    </View>
                )
            })}
            {note != null && note !== "" && (
                <Text size="text-sm" weight="semibold" color="text-muted">
                    {note}
                </Text>
            )}
            {pending ? (
                <WaitingText size="text-sm" color="text-muted" align="start">
                    {TEXTS.pending}
                </WaitingText>
            ) : (
                action != null && <TextButton label={action.label} size="sm" onPress={action.onPress} />
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: (align: "start" | "center") => ({
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        alignItems: "center" as const,
        justifyContent: align === "center" ? ("center" as const) : ("flex-start" as const),
        gap: theme.spacing["2"],
    }),
}))
